<!-- src/components/student/StudentMyCourses.vue -->
<template>
  <div class="my-courses-section">
    <div class="my-courses-container">
      <div class="my-courses-wrapper">
        <!-- Заголовок -->
        <div class="my-courses-header">
          <h2 class="section-title">Мои курсы</h2>
        </div>

        <!-- Загрузка -->
        <LoadingSpinner v-if="loading" text="Загружаем ваши курсы..." :inline="true" />

        <!-- Пусто -->
        <div v-else-if="!courses.length" class="empty-state">
          <svg class="empty-icon" width="64" height="64" viewBox="0 0 24 24" fill="none">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" stroke-width="2"/>
          </svg>
          <p class="empty-text">Вы ещё не записаны ни на один курс</p>
        </div>

        <!-- Слайдер курсов -->
        <div v-else>
          <div class="my-courses-tabs">
            <button
              v-for="tab in tabs"
              :key="tab.value"
              class="my-courses-tab"
              :class="{ 'my-courses-tab--active': activeTab === tab.value }"
              type="button"
              @click="selectTab(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>

          <div v-if="!filteredCourses.length" class="empty-state empty-state--compact">
            <p class="empty-text">Нет курсов в этой категории</p>
          </div>

          <!-- Слайдер курсов -->
          <div v-else class="courses-slider">
            <div 
              class="courses-track" 
              ref="trackRef"
            >
              <div 
                v-for="course in filteredCourses"
                :key="course.id"
                class="course-item"
              >
                <X192
                  :title="course.title"
                  :cover-image="course.coverImage"
                  :progress-percent="course.progressPercent"
                  :current-lesson="course.currentLesson"
                  :total-lessons="course.totalLessons"
                  @click="onCourseClick(course)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Стрелки (только если курсов > 4) -->
        <SliderArrows
          v-if="!loading && filteredCourses.length > 4"
          position="bottom-right"
          :can-scroll-left="canScrollLeft"
          :can-scroll-right="canScrollRight"
          :show="canScrollLeft || canScrollRight"
          @scroll="handleScroll"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick, watch, computed } from 'vue'
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue'
import SliderArrows from '@/components/shared/SliderArrows.vue'
import X192 from '@/components/X192.vue'

export default {
  name: 'StudentMyCourses',

  components: {
    LoadingSpinner,
    SliderArrows,
    X192,
  },

  props: {
    courses: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['go-to-course', 'more-click'],

  setup(props) {
    const trackRef = ref(null)
    const canScrollLeft = ref(false)
    const canScrollRight = ref(false)
    const tabs = [
      { value: 'in_progress', label: 'В процессе' },
      { value: 'paused', label: 'На паузе' },
      { value: 'completed', label: 'Завершенные' },
    ]
    const activeTab = ref('in_progress')

    const toNumber = (value) => {
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : null
    }

    const getCourseState = (course) => {
      const rawStatus = String(
        course?.status ||
          course?.state ||
          course?.course_status ||
          course?.enrollment_status ||
          ''
      ).toLowerCase()

      if (['completed', 'done', 'finished', 'passed'].includes(rawStatus)) {
        return 'completed'
      }
      if (
        ['paused', 'on_hold', 'on-hold', 'hold', 'stopped', 'suspended'].includes(
          rawStatus
        )
      ) {
        return 'paused'
      }

      const progress = toNumber(
        course?.progressPercent ?? course?.progress_percent ?? course?.progress ?? 0
      )
      const currentLesson = toNumber(
        course?.currentLesson ??
          course?.current_lesson ??
          course?.current_lesson_number ??
          0
      )
      const totalLessons = toNumber(
        course?.totalLessons ??
          course?.lessons_total ??
          course?.total_lessons ??
          0
      )

      if (totalLessons && currentLesson !== null && currentLesson >= totalLessons) {
        return 'completed'
      }
      if (progress !== null && progress >= 100) return 'completed'
      return 'in_progress'
    }

    const filteredCourses = computed(() => {
      if (!Array.isArray(props.courses)) return []
      return props.courses.filter(
        course => getCourseState(course) === activeTab.value
      )
    })

    const selectTab = (value) => {
      if (activeTab.value === value) return
      activeTab.value = value
      if (trackRef.value) {
        trackRef.value.scrollTo({ left: 0, behavior: 'smooth' })
      }
      nextTick(() => {
        setTimeout(checkScroll, 100)
      })
    }

    // Проверка возможности скролла
    const checkScroll = () => {
      if (!trackRef.value) return

      const track = trackRef.value
      const scrollLeft = track.scrollLeft
      const scrollWidth = track.scrollWidth
      const clientWidth = track.clientWidth

      canScrollLeft.value = scrollLeft > 10
      canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 10
    }

    // Скролл на 4 курса (1 экран)
    const handleScroll = (direction) => {
      if (!trackRef.value) return

      const track = trackRef.value
      const cardWidth = track.querySelector('.course-item')?.offsetWidth || 0
      const gap = 24
      
      // Скроллим ровно на 4 курса (1 полный экран)
      const scrollAmount = (cardWidth + gap) * 4

      track.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      })

      setTimeout(checkScroll, 350)
    }

    // Инициализация
    onMounted(() => {
      if (trackRef.value) {
        trackRef.value.addEventListener('scroll', checkScroll)
        nextTick(() => {
          setTimeout(checkScroll, 100)
        })
      }

      window.addEventListener('resize', checkScroll)
    })

    onUnmounted(() => {
      if (trackRef.value) {
        trackRef.value.removeEventListener('scroll', checkScroll)
      }
      window.removeEventListener('resize', checkScroll)
    })

    // Проверка при изменении курсов
    watch([() => props.courses.length, activeTab], () => {
      nextTick(() => {
        setTimeout(checkScroll, 100)
      })
    })

    return {
      trackRef,
      canScrollLeft,
      canScrollRight,
      tabs,
      activeTab,
      filteredCourses,
      selectTab,
      handleScroll,
    }
  },

  methods: {
    onCourseClick(course) {
      this.$emit('go-to-course', course)
    },
  },
}
</script>

<style scoped>
.my-courses-section {
  width: 100%;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  padding: 60px 80px;
  min-height: 480px;
}

.my-courses-container {
  max-width: 1600px;
  margin: 0 auto;
}

.my-courses-wrapper {
  position: relative;
}

.my-courses-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.section-title {
  font-family: 'Poppins', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.my-courses-more-btn {
  background: none;
  border: none;
  padding: 10px 16px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #0095ec;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.my-courses-more-btn:hover {
  background: rgba(0, 149, 236, 0.12);
  color: #0077c2;
}

.my-courses-tabs {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 24px;
  padding: 8px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.85), rgba(226, 248, 255, 0.75));
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 999px;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(8px);
}

.my-courses-tab {
  background: transparent;
  border: none;
  padding: 10px 18px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #5b6475;
  border-radius: 999px;
  transition: color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.my-courses-tab--active {
  color: #0b2a3f;
  background: linear-gradient(135deg, #ffffff, #e1f2ff);
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.14);
  transform: translateY(-1px);
}

.my-courses-tab:hover {
  color: #0f172a;
  background: rgba(255, 255, 255, 0.7);
}

.empty-state--compact {
  padding: 24px 0 40px;
  min-height: 140px;
}

/* === СЛАЙДЕР КУРСОВ === */
.courses-slider {
  position: relative;
  overflow: hidden;
}

.courses-track {
  display: grid;
  grid-auto-flow: column;
  /* 4 курса на экране: (100% - 3 промежутка по 24px) / 4 */
  grid-auto-columns: calc((100% - 72px) / 4); /* 72px = 3 * 24px */
  gap: 24px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.courses-track::-webkit-scrollbar {
  display: none;
}

.course-item {
  width: 100%;
  min-width: 0;
  /* Убираем любые margin/padding которые могут мешать */
  margin: 0;
  padding: 0;
}

/* === ПУСТОЕ СОСТОЯНИЕ === */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  min-height: 300px;
}

.empty-icon {
  color: #cbd5e1;
}

.empty-text {
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #64748b;
  margin: 0;
}

/* === АДАПТИВНОСТЬ === */

/* 3 курса на экране (1200-1400px) */
@media (max-width: 1400px) {
  .courses-track {
    grid-auto-columns: calc((100% - 48px) / 3); /* 48px = 2 * 24px */
    gap: 24px;
  }
}

/* 2 курса на экране (768-1200px) */
@media (max-width: 1200px) {
  .my-courses-section {
    padding: 50px 40px;
  }
  
  .courses-track {
    grid-auto-columns: calc((100% - 20px) / 2); /* 20px = 1 * 20px */
    gap: 20px;
  }
}

/* 2 курса на планшете */
@media (max-width: 768px) {
  .my-courses-tabs {
    gap: 10px;
    margin-bottom: 20px;
    padding: 6px;
  }

  .my-courses-tab {
    padding: 6px 12px;
    font-size: 14px;
  }
  .my-courses-section {
    padding: 40px 24px;
  }
  
  .my-courses-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .section-title {
    font-size: 24px;
  }
  
  .my-courses-more-btn {
    align-self: flex-end;
  }
  
  .courses-track {
    grid-auto-columns: calc((100% - 16px) / 2); /* 16px = 1 * 16px */
    gap: 16px;
  }
}

/* 1 курс на мобильном */
@media (max-width: 600px) {
  .my-courses-section {
    padding: 32px 16px;
  }
  
  .section-title {
    font-size: 20px;
  }
  
  .courses-track {
    grid-auto-columns: 100%;
    gap: 16px;
  }
  
  .empty-icon {
    width: 48px;
    height: 48px;
  }
  
  .empty-text {
    font-size: 16px;
  }
}
</style>

















